import { CircleCheck, CirclePlay, Clock, Paperclip } from 'lucide-react';
import type { Avancement, LeconProgramme } from '../../lib/useLecteur';

/**
 * Taux de complétion (TF-451), tel que l'API le donne : arrondi à
 * l'inférieur, `null` quand il n'y a rien à faire — l'écran ne le recalcule
 * jamais.
 */
export function tauxLisible(avancement: Avancement): string {
  return avancement.taux === null ? '—' : `${avancement.taux} %`;
}

/** « 2 leçons terminées sur 3 ». */
export function leconsTerminees(avancement: Avancement): string {
  const s = avancement.faits > 1 ? 's' : '';
  return `${avancement.faits} leçon${s} terminée${s} sur ${avancement.total}`;
}

/** Barre de progression accessible ; vide quand il n'y a rien à faire. */
export function BarreAvancement({
  avancement,
  libelle,
  hauteur = 'h-1.5',
}: {
  avancement: Avancement;
  /** Ce que la barre mesure, lu par les lecteurs d'écran. */
  libelle: string;
  hauteur?: 'h-1' | 'h-1.5' | 'h-2';
}): JSX.Element {
  return (
    <div
      role="progressbar"
      aria-label={libelle}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={avancement.taux ?? undefined}
      aria-valuetext={avancement.taux === null ? 'Rien à suivre' : `${avancement.taux} %`}
      className={`progress-track ${hauteur} overflow-hidden rounded-full`}
    >
      <div className="progress-bar h-full rounded-full" style={{ width: `${avancement.taux ?? 0}%` }} />
    </div>
  );
}

/**
 * L'icône d'une leçon dans un programme : terminée (TF-451), sinon vidéo,
 * ressources ou contenu à venir. La leçon terminée le dit aussi en texte.
 */
export function IconeLecon({
  lecon,
  taille = 'h-4 w-4',
}: {
  lecon: LeconProgramme;
  taille?: 'h-4 w-4' | 'h-5 w-5';
}): JSX.Element {
  if (lecon.terminee) {
    return (
      <>
        <CircleCheck className={`${taille} shrink-0 text-codialis-mint`} aria-hidden />
        <span className="sr-only">Terminée :</span>
      </>
    );
  }
  if (lecon.video) return <CirclePlay className={`${taille} shrink-0 text-codialis-sky`} aria-hidden />;
  if (lecon.ressources > 0) return <Paperclip className={`${taille} shrink-0 text-codialis-sky`} aria-hidden />;
  return <Clock className={`${taille} shrink-0 text-codialis-gray`} aria-hidden />;
}
